@if (!(loading$ | async)) {
    @for (facetHolder of selectedFacets; track facetHolder.facet; let index = $index) {
        <div class="mb-1">
            <app-facet-drop-down
                #facetDropdown
                [value]="facetHolder?.facet"
                (valueChange)="changeFacet(facetHolder, $event, facetDropdown)"
                [placeholder]="'product-variant.select-facet' | translate"
                [dataTestId]="'product-variant-facet-dropdown'"
            ></app-facet-drop-down>
        </div>

        @if (facetDropdown.value()) {
            <app-widget-facet-value-list
                class="table-height"
                #facetValueList
                [noDelete]="true"
                [noBaseEdit]="true"
                [inlineEditMode]="false"
                [noColFilter]="true"
                [noSearch]="true"
                (selectedItemsChange)="onSelectedFacetValuesChange($event, facetHolder)"
                [firstSelectAllSig]="false"
                [facetValuesInputSig]="facetHolder.selectedValues"
                [facetInputSig]="facetDropdown.value()"
                [checkboxMode]="true"
                [paginator]="null"
                [take]="1000"
                [dataTestId]="'product-variant-facet-value-list'"
            ></app-widget-facet-value-list>
        }
        <div class="mt-1 mb-2">
            @if (selectedFacets.length > 1) {
                <p-button class="me-1" (onClick)="onDeleteFacet(index, facetDropdown)">{{
                    'facet.remove-facet' | translate
                }}</p-button>
            }
        </div>
    }
    @if (selectedFacets.length > 0) {
        <p-button
            severity="secondary"
            icon="fa fa-plus"
            label="{{ 'facet.add-facet' | translate }}"
            class="me-1"
            [disabled]="!canAddFacet()"
            (onClick)="addFacetButtonClick()"
        ></p-button>
    }

    <hr class="my-2" />

    <span class="font-bold font-size-lg">{{ 'product.available-variants' | translate }}</span>

    <div class="mt-2">
        <p-table #selectedFacetTable [value]="allVariants()" *ngIf="allVariants()[0]?.variant?.v1">
            <ng-template #header>
                <th>
                    <span class="ms-1">
                        {{ 'product.variants' | translate }}
                    </span>
                </th>
                <th>
                    <span class="ms-1">
                        {{ 'product.variant-product' | translate }}
                    </span>
                </th>
                @if (generateProductsEnabled()) {
                    <th>
                        <span class="ms-1">
                            {{ 'product-variant.preview' | translate }}
                        </span>
                    </th>
                }
                <th>
                    <span class="ms-1">
                        {{ 'common.actions' | translate }}
                    </span>
                </th>
            </ng-template>

            <ng-template #body let-variant let-rowIndex="rowIndex">
                <tr>
                    <td>
                        @if (variant?.variant?.v1?.identifier) {
                            <span class="font-bold">{{ variant?.variant?.v1.identifier }}</span>
                            @if (
                                variant?.variant?.v1.identifier != variant?.variant?.v1.description &&
                                variant?.variant?.v1.description
                            ) {
                                <span class="ms-2">{{ variant?.variant?.v1.description }}</span>
                            }
                        }
                        @if (variant?.variant?.v2?.identifier) {
                            <span class="ms-2"> > </span>
                            <span class="font-bold ms-2">{{
                                variant?.variant?.v2.identifier
                            }}</span>
                            @if (
                                variant?.variant?.v2.identifier != variant?.variant?.v2.description &&
                                variant?.variant?.v2.description
                            ) {
                                <span class="ms-2">{{ variant?.variant?.v2.description }}</span>
                            }
                        }
                        @if (variant?.variant?.v3?.identifier) {
                            <span class="ms-2"> > </span>
                            <span class="font-bold ms-2">{{
                                variant?.variant?.v3.identifier
                            }}</span>
                            @if (
                                variant?.variant?.v3.identifier != variant?.variant?.v3.description &&
                                variant?.variant?.v3.description
                            ) {
                                <span class="ms-2">{{ variant?.variant?.v3.description }}</span>
                            }
                        }
                        @if (!variant?.variant?.v1?.identifier) {
                            <span class="text-gray-500 italic">Invalid variant data</span>
                        }
                    </td>

                    <td class="w-4/12">
                        <span>
                            <app-product-dropdown
                                #productDropdown
                                [noVariantProducts]="true"
                                required="true"
                                [value]="variant?.existingProductVariant"
                                (valueChange)="variant.existingProductVariant = $event"
                                class="ms-2"
                                [dataTestId]="'product-variant-product-dropdown'"
                            ></app-product-dropdown>
                        </span>
                    </td>

                    @if (generateProductsEnabled()) {
                        <td class="align-middle">
                            @let variantPreview = getVariantPreview(variant);
                            @if (variantPreview.result) {
                                <!-- Show generation result -->
                                <div class="flex items-center justify-center gap-2 min-h-[1.5rem]">
                                    @if (variantPreview.result.status === 'generating') {
                                        <i class="fa fa-spinner animate-spin text-gray-500"></i>
                                        <span class="text-gray-600">{{'product-variant.generating-variant-product' | translate}}</span>
                                    } @else if (variantPreview.result.status === 'success') {
                                        <i class="fa fa-check-circle text-green-500 w-3 h-3 flex-shrink-0 text-sm"></i>
                                        <span class="text-green-700">{{'common.created' | translate}}</span>
                                    } @else if (variantPreview.result.status === 'error') {
                                        <i class="fa fa-exclamation-circle text-red-500 w-3 h-3 flex-shrink-0 text-sm"></i>
                                        <span 
                                            class="text-red-700 cursor-help" 
                                            [pTooltip]="variantPreview.result.message"
                                        >
                                            {{'common.failed' | translate}}
                                        </span>
                                    }
                                </div>
                            } @else if (variantPreview.preview) {
                                <!-- Show preview identifier -->
                                <div class="min-h-[1.5rem] flex items-center justify-center">
                                    <span class="text-center w-full">
                                        {{ variantPreview.preview }}
                                    </span>
                                </div>
                            }
                        </td>
                    }

                    <td class="align-middle text-center">
                        <div class="flex flex-row justify-center">
                            <p-button
                                class="me-1"
                                [icon]="'fa fa-floppy-disk'"
                                [disabled]="
                                    !variant?.existingProductVariant?.id ||
                                    !(
                                        variant?.originalProductVariant?.id !=
                                        variant?.existingProductVariant?.id
                                    )
                                "
                                (onClick)="saveVariant(variant)"
                                [pTooltip]="'product-variant.save' | translate"
                                [attr.data-testid]="'product-variant-save-button-' + rowIndex"
                            ></p-button>
                            <p-button
                                [icon]="'fa-solid fa-link-slash'"
                                [disabled]="!variant?.originalProductVariant?.id"
                                [pTooltip]="'product-variant.delete-product-connection' | translate"
                                (onClick)="deleteLinks(variant, productDropdown)"
                                class="me-1"
                                [attr.data-testid]="'product-variant-delete-links-button-' + rowIndex"
                                >
                            </p-button>
                            <!-- open in new tab button -->
                            <p-button
                                [icon]="'fa fa-external-link'"
                                [disabled]="!variant?.existingProductVariant?.id"
                                [pTooltip]="'common.open-in-new-tab' | translate"
                                (onClick)="openInNewTab(variant)"
                                [attr.data-testid]="'product-variant-open-in-new-tab-button-' + rowIndex"
                            ></p-button>
                            <!-- <p-button
                                [icon]="'fa fa-trash'"
                                [disabled]="!variant?.originalProductVariant?.id"
                                [pTooltip]="
                                    'product-variant.delete-product-and-connection' | translate
                                "
                                (onClick)="deleteVariant(variant, productDropdown)"
                            >
                            </p-button> -->
                        </div>
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="footer">
                <!-- Generate Products Toggle -->
                <div class="mt-3 mb-2">
                    <phx-checkbox
                        [(ngModel)]="generateProductsEnabled"
                        [label]="'product-variant.enable-generate-products' | translate"
                        [dataTestId]="'product-variant-enable-generate-products-checkbox'"
                    ></phx-checkbox>
                </div>

                @if (generateProductsEnabled()) {
                    <tr>
                        <td colspan="4" class="p-4">
                            <!-- Product Generation Section -->
                            <div
                                class="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
                            >
                                <h4
                                    class="text-lg font-semibold mb-3 text-blue-800 dark:text-blue-200"
                                >
                                    {{ 'product-variant.generate-products' | translate }}
                                </h4>

                                <!-- Delimiter Configuration -->
                                <div class="mb-4">
                                    <h5
                                        class="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300"
                                    >
                                        {{ 'product-variant.delimiter-configuration' | translate }}
                                    </h5>
                                    <div class="flex items-center gap-2 flex-wrap">
                                        <span class="text-gray-600 dark:text-gray-400">
                                            {{ baseProduct?.identifier || 'P01' }}
                                        </span>

                                        @for (
                                            facetHolder of selectedFacets;
                                            track facetHolder.facet;
                                            let facetIndex = $index
                                        ) {
                                            @if (facetHolder.facet) {
                                                <!-- Delimiter dropdown for this facet -->
                                                <div class="w-48">
                                                    <phx-select
                                                        [items]="delimiterOptions()"
                                                        [value]="
                                                            facetDelimiters()[facetIndex] || ''
                                                        "
                                                        (valueChange)="
                                                            onDelimiterChange(facetIndex, $event)
                                                        "
                                                        optionLabel="label"
                                                        optionValue="value"
                                                        [placeholder]="
                                                            'product-variant.select-delimiter'
                                                                | translate
                                                        "
                                                        [dataTestId]="'product-variant-delimiter-dropdown-' + facetIndex"
                                                        size="small"
                                                    ></phx-select>
                                                </div>

                                                <span class="text-blue-600 dark:text-blue-400">
                                                    [{{
                                                        facetHolder.facet.description ??
                                                            facetHolder.facet.identifier
                                                    }}]
                                                </span>
                                            }
                                        }
                                        <div class="w-48">
                                        <app-generic-enum-drop-down
                                            enumName="ProductType"
                                            [(value)]="generateProductType"
                                            optionLabel="label"
                                            optionValue="value"
                                            size="small"
                                            [placeholder]="'product-variant.select-product-type' | translate"
                                        ></app-generic-enum-drop-down>
                                        </div>
                                    </div>
                                </div>

                                <!-- Generate Button -->
                                <div class="flex gap-2">
                                    <p-button
                                        [icon]="isGenerating() ? 'fa fa-spinner animate-spin' : 'fa fa-magic'"
                                        label="{{
                                            'product-variant.generate-products-for-unlinked-variants'
                                                | translate
                                        }}"
                                        severity="primary"
                                        [disabled]="!canGenerateProducts() || isGenerating()"
                                        (onClick)="generateProductsForVariants()"
                                        [pTooltip]="'product-variant.generate-products-tooltip' | translate"
                                        [attr.data-testid]="'product-variant-generate-products-button'"
                                    ></p-button>
                                    
                                    @if (hasGenerationResults()) {
                                        <p-button
                                            icon="fa fa-times"
                                            label="{{ 'product-variant.clear-results' | translate }}"
                                            severity="secondary"
                                            (onClick)="clearGenerationResults()"
                                            [pTooltip]="'product-variant.clear-generation-results' | translate"
                                        ></p-button>
                                    }
                                </div>
                            </div>

                            <!-- Existing Actions Section -->
                            <div class="flex gap-2 items-center">
                                <span class="font-bold me-1"
                                    >{{ 'product-variant.all-variants' | translate }}
                                </span>
                                <div class="flex flex-row gap-2">
                                    <p-button
                                        class=""
                                        [disabled]="!qualifyForSaveAll()"
                                        [icon]="'fa fa-floppy-disk'"
                                        [pTooltip]="'product-variant.save-all-variants' | translate"
                                        (onClick)="saveAllVariants()"
                                        [attr.data-testid]="'product-variant-save-all-variants-button'"
                                    >
                                    </p-button>
                                    <p-button
                                        class=""
                                        [disabled]="!qualifyForConnectionAndDelete()"
                                        [icon]="'fa-solid fa-link-slash'"
                                        [pTooltip]="
                                            'product-variant.delete-all-product-connections'
                                                | translate
                                        "
                                        [attr.data-testid]="'product-variant-delete-all-connections-button'"
                                        (onClick)="deleteAllLinks()"
                                    ></p-button>
                                    <!-- <p-button
                                        class=""
                                        [disabled]="!qualifyForConnectionAndDelete()"
                                        [icon]="'fa fa-trash'"
                                        [pTooltip]="
                                            'product-variant.delete-all-products-and-connections'
                                                | translate
                                        "
                                        (onClick)="deleteAllVariants()"
                                    >
                                    </p-button> -->
                                </div>
                            </div>
                        </td>
                    </tr>
                } @else {
                    <tr>
                        <td colspan="3" class="p-4">
                            <!-- Existing Actions Section -->
                            <div class="flex gap-2 items-center">
                                <span class="font-bold me-1"
                                    >{{ 'product-variant.all-variants' | translate }}
                                </span>
                                <div class="flex flex-row gap-2">
                                    <p-button
                                        class=""
                                        [disabled]="!qualifyForSaveAll()"
                                        [icon]="'fa fa-floppy-disk'"
                                        [pTooltip]="'product-variant.save-all-variants' | translate"
                                        (onClick)="saveAllVariants()"
                                    >
                                    </p-button>
                                    <p-button
                                        class=""
                                        [disabled]="!qualifyForConnectionAndDelete()"
                                        [icon]="'fa-solid fa-link-slash'"
                                        [pTooltip]="
                                            'product-variant.delete-all-product-connections'
                                                | translate
                                        "
                                        (onClick)="deleteAllLinks()"
                                    ></p-button>
                                    <!-- <p-button
                                        class=""
                                        [disabled]="!qualifyForConnectionAndDelete()"
                                        [icon]="'fa fa-trash'"
                                        [pTooltip]="
                                            'product-variant.delete-all-products-and-connections'
                                                | translate
                                        "
                                        (onClick)="deleteAllVariants()"
                                    >
                                    </p-button> -->
                                </div>
                            </div>
                        </td>
                    </tr>
                }
            </ng-template>
        </p-table>
    </div>
} @else {
    <div class="flex justify-center">
        <p-progressSpinner styleClass="mt-8"></p-progressSpinner>
    </div>
}
